import { useState } from 'react'
import type { Card } from '../../types'
import CardFront from './CardFront'
import CardBack from './CardBack'

interface CardViewProps {
  card: Card
  onNext: () => void
  onChoice: (cardId: string, choiceIndex: number, timeSpentMs: number) => void
  progress: { current: number; total: number }
}

function CardView({ card, onNext, onChoice, progress }: CardViewProps) {
  const [isFlipped, setIsFlipped] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [startTime] = useState(Date.now())

  const handleChoice = (index: number) => {
    setSelectedIndex(index)
    const timeSpentMs = Date.now() - startTime
    onChoice(card.id, index, timeSpentMs)
    // 不再自动翻面，等待用户点 "REVEAL" 按钮手动触发
  }

  const handleReveal = () => {
    setIsFlipped(true)
  }

  const handleNext = () => {
    setIsFlipped(false)
    setSelectedIndex(null)
    onNext()
  }

  // 对于 concept 类型（无选择），直接显示正面信息，点击翻面
  const isConceptCard = card.choices.length === 0
  const progressPercent = progress.total > 0 ? (progress.current / progress.total) * 100 : 0

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* ========== 顶部 HUD ========== */}
      <div className="pixel-box p-2 mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
            ▶
          </span>
          <span className="font-pixel-display text-[10px] text-[color:var(--color-cyan)]">
            第 {String(progress.current).padStart(2, '0')} / {String(progress.total).padStart(2, '0')} 题
          </span>
        </div>

        {/* 进度条 */}
        <div className="flex-1 min-w-[100px] max-w-xs">
          <div className="pixel-progress">
            <div
              className="pixel-progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="font-pixel-display text-[10px] text-[color:var(--color-gold)]">
          {Math.round(progressPercent)}%
        </div>
      </div>

      {/* ========== 卡片容器 ========== */}
      <div className="pixel-box pixel-box-bright overflow-hidden">
        {/* 翻牌动画容器 */}
        <div
          className="relative transition-transform"
          style={{
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            transitionDuration: '600ms',
            transitionTimingFunction: 'steps(20)',
          }}
        >
          {/* 正面 */}
          <div
            style={{
              backfaceVisibility: 'hidden',
              position: isFlipped ? 'absolute' : 'relative',
              width: '100%',
              top: 0,
            }}
          >
            <CardFront
              card={card}
              onChoice={handleChoice}
              selectedIndex={selectedIndex}
            />
            {!isFlipped && (
              <div className="px-5 pb-5 relative">
                {/* 浮动提示标签 - 答完题后才显示 */}
                {selectedIndex !== null && !isConceptCard && (
                  <div className="absolute -top-3 -right-2 pixel-box pixel-box-bright bg-[color:var(--color-bg-elevated)] px-2 py-1 z-10">
                    <span className="font-pixel text-[9px] text-[color:var(--color-gold)] tracking-wider">
                      ► 点这里翻面
                    </span>
                  </div>
                )}
                <button
                  onClick={handleReveal}
                  disabled={!isConceptCard && selectedIndex === null}
                  className={
                    selectedIndex !== null || isConceptCard
                      ? 'pixel-button pixel-button-primary w-full pixel-blink-soft'
                      : 'pixel-button pixel-button-primary w-full disabled:opacity-50 disabled:cursor-not-allowed'
                  }
                >
                  {isConceptCard
                    ? '► 翻开看答案'
                    : selectedIndex === null
                      ? '► 请先选一个答案'
                      : '► 翻开看答案'}
                </button>
              </div>
            )}
          </div>

          {/* 背面 */}
          <div
            style={{
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              position: isFlipped ? 'relative' : 'absolute',
              width: '100%',
              top: 0,
            }}
          >
            <CardBack
              card={card}
              wasCorrect={selectedIndex !== null && (card.choices[selectedIndex]?.isCorrect ?? false)}
              onNext={handleNext}
            />
          </div>
        </div>
      </div>

      {/* ========== 底部提示 ========== */}
      <div className="mt-3 text-center font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
        {isFlipped
          ? '► 看答案 · 准备好了点 [下一题]'
          : selectedIndex !== null || isConceptCard
            ? '► 点 [翻开看答案] 按钮手动翻面'
            : '► 点 [A][B][C][D] 或直接点选项'}
      </div>
    </div>
  )
}

export default CardView